/**
 * Review Settings Service (POS / owner side)
 *
 * The single config for the review-request system. Everything is configurable —
 * Jose's mandate: never hardcode a timeframe, give options, because licensees run
 * different rhythms. Stored staff-side at settings/reviews; the customer-facing subset
 * is mirrored to publicConfig/reviewSettings (public-read) which the funnel page reads
 * (mirrors the mirrorPublicHours pattern in shopSettingsService).
 *
 * BEHAVIOR-PIN: SLIK review-request system — project-slik-review-request-system-2026-08-26.
 */
import { doc, getDoc, setDoc, onSnapshot, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';

export type ReviewChannel = 'sms' | 'email' | 'both';

export interface ReviewSettings {
  enabled: boolean;
  channel: ReviewChannel;

  // Where "leave a Google review" goes — paste the g.page link (preferred), else Place ID.
  googleReviewLink: string;
  googlePlaceId: string;

  // Funnel gate
  publicThreshold: number;        // rating ≥ this → push to Google (default 5)
  privateFeedbackEnabled: boolean;

  // Timing after checkout (configurable presets live in the UI)
  delayMinutes: number;           // default 15
  quietStart: string;             // 'HH:MM' — no sends before
  quietEnd: string;               // 'HH:MM' — no sends after

  // Cadence — anti-bombard. Both mechanisms available + combinable (Jose 2026-08-26).
  minDaysBetweenAsks: number;     // default 60 (presets 30/60/90/custom in UI)
  useVisitsSinceAsk: boolean;     // off by default; licensees may use
  visitsSinceAsk: number;         // e.g. 3
  newClientFirstVisit: boolean;   // ask a brand-new client after their 1st visit

  // Reminder — configurable: off (one-and-done) OR one nudge
  reminderEnabled: boolean;       // default false
  reminderDelayDays: number;      // default 3

  // Funnel copy (mirrored to public)
  shopName: string;
  headline: string;
  thankYouPublic: string;
  thankYouPrivate: string;

  // Message templates (tokens: {name} {barber} {shop} {link})
  smsTemplate: string;
  emailSubject: string;
  emailBody: string;

  updatedAt: string;
}

export const DEFAULT_REVIEW_SETTINGS: ReviewSettings = {
  enabled: false,                 // owner turns it on when ready to send
  channel: 'both',
  googleReviewLink: 'https://g.page/r/CVhCEdueRX6eEBM/review',
  googlePlaceId: '',
  publicThreshold: 5,
  privateFeedbackEnabled: true,
  delayMinutes: 15,
  quietStart: '21:00',
  quietEnd: '09:00',
  minDaysBetweenAsks: 60,
  useVisitsSinceAsk: false,
  visitsSinceAsk: 3,
  newClientFirstVisit: true,
  reminderEnabled: false,
  reminderDelayDays: 3,
  shopName: 'SLIK Barber Lounge',
  headline: 'How was your visit?',
  thankYouPublic: 'Thank you! A quick public review means the world to us. 🙏',
  thankYouPrivate: "Thank you — we're sorry it wasn't perfect. Your note goes straight to the owner.",
  smsTemplate: "Hey {name}, thanks for visiting {shop}! How was your cut with {barber}? {link}",
  emailSubject: 'How was your visit to {shop}?',
  emailBody: "Hi {name},\n\nThanks for coming in! We'd love to hear how {barber} did — it only takes a few seconds:\n{link}\n\n— {shop}",
  updatedAt: new Date().toISOString(),
};

/** Subset the customer-facing funnel reads (publicConfig/reviewSettings). */
function publicSubset(s: ReviewSettings) {
  return {
    enabled: s.enabled,
    googleReviewLink: s.googleReviewLink,
    googlePlaceId: s.googlePlaceId,
    publicThreshold: s.publicThreshold,
    privateFeedbackEnabled: s.privateFeedbackEnabled,
    shopName: s.shopName,
    headline: s.headline,
    thankYouPublic: s.thankYouPublic,
    thankYouPrivate: s.thankYouPrivate,
    updatedAt: s.updatedAt,
  };
}

class ReviewSettingsService {
  private ref = doc(db, 'settings', 'reviews');
  private cache: ReviewSettings | null = null;

  async get(): Promise<ReviewSettings> {
    if (this.cache) return this.cache;
    try {
      const snap = await getDoc(this.ref);
      this.cache = snap.exists()
        ? { ...DEFAULT_REVIEW_SETTINGS, ...(snap.data() as Partial<ReviewSettings>) }
        : DEFAULT_REVIEW_SETTINGS;
    } catch {
      this.cache = DEFAULT_REVIEW_SETTINGS;
    }
    return this.cache;
  }

  getCachedSync(): ReviewSettings {
    return this.cache || DEFAULT_REVIEW_SETTINGS;
  }

  /**
   * Save owner settings + mirror the public subset.
   * H15 FIX (Jose 2026-10-29 audit): the two writes used to be SEQUENTIAL setDoc()s with no
   * transaction — if the staff doc committed but the public mirror failed (permission/network), the
   * admin saw "Saved" while the customer funnel read stale data (the two docs diverged). A writeBatch
   * commits BOTH atomically: either both land or neither does, so they can never drift.
   */
  async save(settings: ReviewSettings): Promise<boolean> {
    try {
      const full = { ...settings, updatedAt: new Date().toISOString() };
      const batch = writeBatch(db);
      batch.set(this.ref, full);
      batch.set(doc(db, 'publicConfig', 'reviewSettings'), publicSubset(full));
      await batch.commit();
      this.cache = full;
      return true;
    } catch (e) {
      console.error('reviewSettings save failed', e);
      return false;
    }
  }

  subscribe(cb: (s: ReviewSettings) => void): () => void {
    return onSnapshot(this.ref, (snap) => {
      this.cache = snap.exists()
        ? { ...DEFAULT_REVIEW_SETTINGS, ...(snap.data() as Partial<ReviewSettings>) }
        : DEFAULT_REVIEW_SETTINGS;
      cb(this.cache);
    }, (err) => console.warn('[reviewSettings] subscription error:', (err as any)?.message || err));
  }
}

export const reviewSettingsService = new ReviewSettingsService();
export default reviewSettingsService;
